import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import Sidebar from "@/components/Sidebar";
import axios from "axios";
import { toast } from "sonner";
import { 
  Shield, 
  Users, 
  BookOpen, 
  ImageIcon, 
  Film, 
  FileText,
  CreditCard,
  TrendingUp,
  UserCheck,
  UserX,
  Trash2,
  Loader2,
  RefreshCw,
  Crown,
  Ban,
  DollarSign,
  Save,
  Gift,
  Sparkles,
  Play,
  Pause,
  Plus,
  RotateCcw,
  Percent,
  Clock,
  Calendar
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPortal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [books, setBooks] = useState([]);
  const [covers, setCovers] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editCredits, setEditCredits] = useState("");
  const [editReason, setEditReason] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [savingPricing, setSavingPricing] = useState(false);
  
  // Password reset states
  const [showPasswordResetDialog, setShowPasswordResetDialog] = useState(false);
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Promo states
  const [promos, setPromos] = useState([]);
  const [showCreatePromoDialog, setShowCreatePromoDialog] = useState(false);
  const [newPromo, setNewPromo] = useState({
    name: "",
    promo_type: "bogo",
    purchase_limit: 1000,
    discount_percent: 20,
    start_date: "",
    end_date: ""
  });
  const [savingPromo, setSavingPromo] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  useEffect(() => {
    checkAdminAndLoad();
  }, []);

  const checkAdminAndLoad = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Check if user is admin
      const meResponse = await axios.get(`${API}/auth/me`, config);
      if (!meResponse.data?.is_admin) {
        toast.error("Admin access required");
        navigate("/dashboard");
        return;
      }
      setIsAdmin(true);
      
      // Load admin data
      await loadAllData();
    } catch (error) {
      console.error("Admin check failed:", error);
      toast.error("Access denied");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const loadAllData = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [statsRes, usersRes, booksRes, coversRes, scriptsRes, pricingRes, promosRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, config),
        axios.get(`${API}/admin/users`, config),
        axios.get(`${API}/admin/content/books`, config),
        axios.get(`${API}/admin/content/covers`, config),
        axios.get(`${API}/admin/content/scripts`, config),
        axios.get(`${API}/pricing`, config),
        axios.get(`${API}/admin/promos`, config).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setBooks(booksRes.data.books || []);
      setCovers(coversRes.data.covers || []);
      setScripts(scriptsRes.data.scripts || []);
      setPricingTiers(pricingRes.data.tiers || []);
      setPromos(promosRes.data || []);
      toast.success("Data refreshed");
    } catch (error) {
      console.error("Failed to load admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setRefreshing(false);
    }
  };

  const handleUpdateCredits = async () => {
    if (!selectedUser || !editCredits) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/admin/users/credits`,
        { user_id: selectedUser.id, credits: parseFloat(editCredits), reason: editReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Credits updated for ${selectedUser.email}`);
      setShowEditDialog(false);
      setSelectedUser(null);
      setEditCredits("");
      setEditReason("");
      await loadAllData();
    } catch (error) {
      toast.error("Failed to update credits");
    }
  };

  const handleToggleAdmin = async (user) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/admin/users/status`,
        { user_id: user.id, is_admin: !user.is_admin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Admin status updated for ${user.email}`);
      await loadAllData();
    } catch (error) {
      toast.error("Failed to update admin status");
    }
  };

  const handleToggleSuspend = async (user) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/admin/users/status`,
        { user_id: user.id, is_suspended: !user.is_suspended },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${user.is_suspended ? 'Activated' : 'Suspended'} ${user.email}`);
      await loadAllData();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const handleDeleteUser = async (user) => {
    setUserToDelete(user);
    setShowDeleteDialog(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem("token");
      console.log("Deleting user:", userToDelete.id, userToDelete.email);
      const response = await axios.delete(`${API}/admin/users/${userToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log("Delete response:", response.data);
      
      toast.success(`Deleted ${userToDelete.email} and all their content`);
      setShowDeleteDialog(false);
      setUserToDelete(null);
      await loadAllData();
    } catch (error) {
      console.error("Delete error:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!passwordResetUser || !newPassword) {
      toast.error("Please enter a new password");
      return;
    }
    
    if (newPassword.length < 7) {
      toast.error("Password must be at least 7 characters");
      return;
    }
    
    setResettingPassword(true);
    try {
      const token = localStorage.getItem("token");
      console.log("Sending password reset request:", {
        user_id: passwordResetUser.id,
        email: passwordResetUser.email,
        new_password_length: newPassword.length
      });
      
      const response = await axios.put(`${API}/admin/users/reset-password`, {
        user_id: passwordResetUser.id,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log("Password reset response:", response.data);
      toast.success(`Password reset successfully for ${passwordResetUser.email}`);
      alert(`SUCCESS! Password has been reset for ${passwordResetUser.email}. The user can now login with the new password.`);
      setShowPasswordResetDialog(false);
      setPasswordResetUser(null);
      setNewPassword("");
    } catch (error) {
      console.error("Password reset error:", error);
      console.error("Error response:", error.response?.data);
      const errorMsg = error.response?.data?.detail || "Failed to reset password";
      toast.error(errorMsg);
      alert(`ERROR: ${errorMsg}`);
    } finally {
      setResettingPassword(false);
    }
  };

  const updatePricingTier = (index, field, value) => {
    const newTiers = [...pricingTiers];
    newTiers[index] = { ...newTiers[index], [field]: field === 'credits' || field === 'price' ? parseInt(value) || 0 : value };
    
    // Auto-calculate perBook
    if (field === 'credits' || field === 'price') {
      const credits = field === 'credits' ? parseInt(value) || 1 : newTiers[index].credits;
      const price = field === 'price' ? parseInt(value) || 0 : newTiers[index].price;
      newTiers[index].perBook = `$${Math.round(price / credits)}/book`;
    }
    
    setPricingTiers(newTiers);
  };

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/pricing`, { tiers: pricingTiers }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Pricing updated successfully");
    } catch (error) {
      console.error("Failed to save pricing:", error);
      toast.error(error.response?.data?.detail || "Failed to save pricing");
    } finally {
      setSavingPricing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-stone-50 to-neutral-100">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-lg font-medium text-stone-700">Loading Admin Portal...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-stone-50 to-neutral-100">
      <Sidebar onLogout={handleLogout} />
      
      <div className="ml-0 md:ml-64 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold playfair text-stone-900 mb-2 flex items-center gap-3">
                <Shield className="h-10 w-10 text-purple-600" />
                Admin Portal
              </h1>
              <p className="text-stone-600">
                Manage users, view analytics, and monitor content
              </p>
            </div>
            <Button onClick={loadAllData} variant="outline" disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          <Tabs defaultValue="dashboard" className="space-y-6">
            <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-flex">
              <TabsTrigger value="dashboard" className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Dashboard
              </TabsTrigger>
              <TabsTrigger value="users" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Users
              </TabsTrigger>
              <TabsTrigger value="books" className="flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Books
              </TabsTrigger>
              <TabsTrigger value="covers" className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                Covers
              </TabsTrigger>
              <TabsTrigger value="scripts" className="flex items-center gap-2">
                <Film className="h-4 w-4" />
                Scripts
              </TabsTrigger>
              <TabsTrigger value="pricing" className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Pricing
              </TabsTrigger>
              <TabsTrigger value="promos" className="flex items-center gap-2">
                <Gift className="h-4 w-4" />
                Promos
              </TabsTrigger>
            </TabsList>

            {/* Dashboard Tab */}
            <TabsContent value="dashboard">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Total Users
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-4xl font-bold">{stats?.users?.total || 0}</p>
                    <p className="text-sm opacity-80">+{stats?.users?.recent_7_days || 0} this week</p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BookOpen className="h-5 w-5" />
                      Total Books
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-4xl font-bold">{stats?.content?.books?.total || 0}</p>
                    <p className="text-sm opacity-80">{stats?.content?.books?.completed || 0} completed</p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ImageIcon className="h-5 w-5" />
                      Cover Projects
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-4xl font-bold">{stats?.content?.covers || 0}</p>
                    <p className="text-sm opacity-80">{stats?.content?.scripts || 0} scripts</p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Credits in System
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-4xl font-bold">{stats?.credits?.total_in_system?.toFixed(0) || 0}</p>
                    <p className="text-sm opacity-80">{stats?.users?.admins || 0} admins</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Content Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="flex items-center gap-2"><BookOpen className="h-4 w-4" /> Books</span>
                        <span className="font-bold">{stats?.content?.books?.total || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="flex items-center gap-2"><FileText className="h-4 w-4" /> Series</span>
                        <span className="font-bold">{stats?.content?.series || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="flex items-center gap-2"><ImageIcon className="h-4 w-4" /> Covers</span>
                        <span className="font-bold">{stats?.content?.covers || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="flex items-center gap-2"><Film className="h-4 w-4" /> Scripts</span>
                        <span className="font-bold">{stats?.content?.scripts || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>User Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="flex items-center gap-2"><Users className="h-4 w-4" /> Total Users</span>
                        <span className="font-bold">{stats?.users?.total || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="flex items-center gap-2"><Crown className="h-4 w-4 text-purple-600" /> Admins</span>
                        <span className="font-bold">{stats?.users?.admins || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="flex items-center gap-2"><Ban className="h-4 w-4 text-red-600" /> Suspended</span>
                        <span className="font-bold">{stats?.users?.suspended || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="flex items-center gap-2"><UserCheck className="h-4 w-4 text-green-600" /> New (7 days)</span>
                        <span className="font-bold">{stats?.users?.recent_7_days || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users">
              <Card>
                <CardHeader>
                  <CardTitle>User Management</CardTitle>
                  <CardDescription>View and manage all registered users</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-auto max-h-[600px] border rounded-lg">
                    <Table className="min-w-[1000px]">
                      <TableHeader className="sticky top-0 bg-white z-10">
                        <TableRow>
                          <TableHead className="min-w-[180px]">User</TableHead>
                          <TableHead className="min-w-[80px]">Credits</TableHead>
                          <TableHead className="min-w-[100px]">Content</TableHead>
                          <TableHead className="min-w-[100px]">Status</TableHead>
                          <TableHead className="min-w-[100px]">Joined</TableHead>
                          <TableHead className="min-w-[400px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {users.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-stone-500">
                              No users found
                              </TableCell>
                          </TableRow>
                        ) : users.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{user.name}</p>
                                <p className="text-sm text-stone-500">{user.email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{user.credits?.toFixed(1) || 0}</Badge>
                            </TableCell>
                            <TableCell>
                              <div className="text-xs space-y-1">
                                <p>{user.stats?.books || 0} books</p>
                                <p>{user.stats?.covers || 0} covers</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                {user.is_admin && (
                                  <Badge className="bg-purple-100 text-purple-800">Admin</Badge>
                                )}
                                {user.is_suspended && (
                                  <Badge className="bg-red-100 text-red-800">Suspended</Badge>
                                )}
                                {!user.is_admin && !user.is_suspended && (
                                  <Badge className="bg-green-100 text-green-800">Active</Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {new Date(user.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setEditCredits(user.credits?.toString() || "0");
                                    setShowEditDialog(true);
                                  }}
                                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                >
                                  <CreditCard className="h-4 w-4 mr-1" />
                                  Credits
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleToggleAdmin(user)}
                                  className={user.is_admin ? "text-purple-600 border-purple-200 hover:bg-purple-50" : "text-stone-500 border-stone-200 hover:bg-stone-50"}
                                >
                                  <Crown className="h-4 w-4 mr-1" />
                                  {user.is_admin ? "Admin" : "User"}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleToggleSuspend(user)}
                                  className={user.is_suspended ? "text-green-600 border-green-200 hover:bg-green-50" : "text-amber-600 border-amber-200 hover:bg-amber-50"}
                                >
                                  {user.is_suspended ? (
                                    <>
                                      <UserCheck className="h-4 w-4 mr-1" />
                                      Activate
                                    </>
                                  ) : (
                                    <>
                                      <UserX className="h-4 w-4 mr-1" />
                                      Suspend
                                    </>
                                  )}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    console.log("Reset PW clicked for user:", user.email, user.id);
                                    alert(`Opening password reset for: ${user.email}`);
                                    setPasswordResetUser(user);
                                    setNewPassword("");
                                    setShowPasswordResetDialog(true);
                                  }}
                                  className="text-orange-600 border-orange-200 hover:bg-orange-50"
                                  data-testid="reset-pw-button"
                                >
                                  <RotateCcw className="h-4 w-4 mr-1" />
                                  Reset PW
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteUser(user)}
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Books Tab */}
            <TabsContent value="books">
              <Card>
                <CardHeader>
                  <CardTitle>All Books ({books.length})</CardTitle>
                  <CardDescription>View all books created on the platform</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-auto max-h-[600px] border rounded-lg">
                    <Table className="min-w-[800px]">
                      <TableHeader className="sticky top-0 bg-white z-10">
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead>User</TableHead>
                          <TableHead>Genre</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Chapters</TableHead>
                          <TableHead>Created</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {books.map((book) => (
                          <TableRow key={book.id}>
                            <TableCell className="font-medium">{book.title}</TableCell>
                            <TableCell>
                              <div>
                                <p className="text-sm">{book.user_name}</p>
                                <p className="text-xs text-stone-500">{book.user_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>{book.genre}</TableCell>
                            <TableCell>
                              <Badge variant={book.status === "completed" ? "default" : "outline"}>
                                {book.status}
                              </Badge>
                            </TableCell>
                            <TableCell>{book.chapters_completed || 0}/{book.total_chapters || 0}</TableCell>
                            <TableCell>{new Date(book.created_at).toLocaleDateString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Covers Tab */}
            <TabsContent value="covers">
              <Card>
                <CardHeader>
                  <CardTitle>All Cover Projects ({covers.length})</CardTitle>
                  <CardDescription>View all cover projects created on the platform</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-auto max-h-[600px] border rounded-lg">
                    <Table className="min-w-[800px]">
                      <TableHeader className="sticky top-0 bg-white z-10">
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead>Author</TableHead>
                          <TableHead>User</TableHead>
                          <TableHead>Genre</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Created</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {covers.map((cover) => (
                          <TableRow key={cover.id}>
                            <TableCell className="font-medium">{cover.title}</TableCell>
                            <TableCell>{cover.author_name}</TableCell>
                            <TableCell className="text-sm text-stone-500">{cover.user_email}</TableCell>
                            <TableCell>{cover.genre}</TableCell>
                            <TableCell>
                              <Badge variant={cover.status === "completed" ? "default" : "outline"}>
                                {cover.status}
                              </Badge>
                            </TableCell>
                            <TableCell>{new Date(cover.created_at).toLocaleDateString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Scripts Tab */}
            <TabsContent value="scripts">
              <Card>
                <CardHeader>
                  <CardTitle>All Scripts ({scripts.length})</CardTitle>
                  <CardDescription>View all scripts</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-semibold mb-3">Scripts</h3>
                      <div className="overflow-auto max-h-[400px] border rounded-lg">
                        <Table className="min-w-[600px]">
                          <TableHeader className="sticky top-0 bg-white z-10">
                            <TableRow>
                              <TableHead>Title</TableHead>
                              <TableHead>User</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Created</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {scripts.map((script) => (
                              <TableRow key={script.id}>
                                <TableCell className="font-medium">{script.title}</TableCell>
                                <TableCell className="text-sm text-stone-500">{script.user_email}</TableCell>
                                <TableCell>
                                  <Badge variant={script.status === "completed" ? "default" : "outline"}>
                                    {script.status}
                                  </Badge>
                                </TableCell>
                                <TableCell>{new Date(script.created_at).toLocaleDateString()}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Pricing Tab */}
            <TabsContent value="pricing">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>Pricing Management</CardTitle>
                      <CardDescription>Configure credit package pricing</CardDescription>
                    </div>
                    <Button onClick={savePricing} disabled={savingPricing}>
                      {savingPricing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save Pricing
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {pricingTiers.map((tier, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-stone-50">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="space-y-2">
                            <Label>Books (Credits)</Label>
                            <Input
                              type="number"
                              value={tier.credits}
                              onChange={(e) => updatePricingTier(index, 'credits', e.target.value)}
                              min="1"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Price ($)</Label>
                            <Input
                              type="number"
                              value={tier.price}
                              onChange={(e) => updatePricingTier(index, 'price', e.target.value)}
                              min="0"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Label</Label>
                            <Input
                              value={tier.label}
                              onChange={(e) => updatePricingTier(index, 'label', e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Description</Label>
                            <Input
                              value={tier.description}
                              onChange={(e) => updatePricingTier(index, 'description', e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                          <div className="space-y-2">
                            <Label>Savings Text</Label>
                            <Input
                              value={tier.savings || ''}
                              onChange={(e) => updatePricingTier(index, 'savings', e.target.value)}
                              placeholder="e.g., Save $24"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Per Book</Label>
                            <Input
                              value={tier.perBook}
                              disabled
                              className="bg-stone-100"
                            />
                          </div>
                          <div className="space-y-2 flex items-end">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={tier.popular || false}
                                onChange={(e) => updatePricingTier(index, 'popular', e.target.checked)}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Mark as Popular</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Promos Tab */}
            <TabsContent value="promos">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Gift className="h-5 w-5 text-green-600" />
                      Promotions Management
                    </CardTitle>
                    <CardDescription>
                      Create and manage BOGO and percentage discount promotions
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowCreatePromoDialog(true)} className="bg-green-600 hover:bg-green-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Promo
                  </Button>
                </CardHeader>
                <CardContent>
                  {promos.length === 0 ? (
                    <div className="text-center py-12 text-stone-500">
                      <Gift className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No promotions created yet</p>
                      <p className="text-sm">Click &quot;Create Promo&quot; to set up your first promotion</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {promos.map((promo) => (
                        <Card key={promo.id} className={`border-2 ${promo.is_active ? 'border-green-500 bg-green-50' : 'border-stone-200'}`}>
                          <CardContent className="py-4">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                              <div className="flex items-center gap-4">
                                <div className={`p-3 rounded-full ${promo.is_active ? 'bg-green-500' : 'bg-stone-300'}`}>
                                  {promo.promo_type === 'bogo' ? (
                                    <Gift className="h-6 w-6 text-white" />
                                  ) : (
                                    <Percent className="h-6 w-6 text-white" />
                                  )}
                                </div>
                                <div>
                                  <h3 className="font-semibold text-lg flex items-center gap-2">
                                    {promo.name}
                                    {promo.is_active && (
                                      <Badge className="bg-green-500">ACTIVE</Badge>
                                    )}
                                  </h3>
                                  <p className="text-stone-600">
                                    {promo.promo_type === 'bogo' ? (
                                      <>Buy One Get One Free</>
                                    ) : (
                                      <>{promo.discount_percent}% discount on all purchases</>
                                    )}
                                  </p>
                                  {(promo.start_date || promo.end_date) && (
                                    <p className="text-sm text-stone-500 mt-1">
                                      {promo.start_date && `Starts: ${new Date(promo.start_date).toLocaleDateString()}`}
                                      {promo.start_date && promo.end_date && ' • '}
                                      {promo.end_date && `Ends: ${new Date(promo.end_date).toLocaleDateString()}`}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {promo.promo_type === 'bogo' && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={async () => {
                                      try {
                                        const token = localStorage.getItem("token");
                                        await axios.put(`${API}/admin/promos/${promo.id}/reset`, {}, {
                                          headers: { Authorization: `Bearer ${token}` }
                                        });
                                        toast.success("Counter reset");
                                        loadAllData();
                                      } catch (error) {
                                        toast.error("Failed to reset counter");
                                      }
                                    }}
                                  >
                                    <RotateCcw className="h-4 w-4 mr-1" />
                                    Reset
                                  </Button>
                                )}
                                <Button
                                  variant={promo.is_active ? "outline" : "default"}
                                  size="sm"
                                  className={promo.is_active ? "text-orange-600 border-orange-600" : "bg-green-600 hover:bg-green-700"}
                                  onClick={async () => {
                                    try {
                                      const token = localStorage.getItem("token");
                                      const action = promo.is_active ? 'deactivate' : 'activate';
                                      await axios.put(`${API}/admin/promos/${promo.id}/${action}`, {}, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      toast.success(`Promo ${action}d`);
                                      loadAllData();
                                    } catch (error) {
                                      toast.error("Failed to update promo");
                                    }
                                  }}
                                >
                                  {promo.is_active ? (
                                    <>
                                      <Pause className="h-4 w-4 mr-1" />
                                      Deactivate
                                    </>
                                  ) : (
                                    <>
                                      <Play className="h-4 w-4 mr-1" />
                                      Activate
                                    </>
                                  )}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={async () => {
                                    if (!window.confirm("Delete this promo?")) return;
                                    try {
                                      const token = localStorage.getItem("token");
                                      await axios.delete(`${API}/admin/promos/${promo.id}`, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      toast.success("Promo deleted");
                                      loadAllData();
                                    } catch (error) {
                                      toast.error("Failed to delete promo");
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Create Promo Dialog */}
          <Dialog open={showCreatePromoDialog} onOpenChange={setShowCreatePromoDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Promotion</DialogTitle>
                <DialogDescription>
                  Set up a BOGO or percentage discount promotion
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="promoName">Promo Name</Label>
                  <Input
                    id="promoName"
                    value={newPromo.name}
                    onChange={(e) => setNewPromo({...newPromo, name: e.target.value})}
                    placeholder="e.g., Launch BOGO, Labor Day Sale"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Promo Type</Label>
                  <div className="flex gap-4">
                    <Button
                      type="button"
                      variant={newPromo.promo_type === 'bogo' ? 'default' : 'outline'}
                      onClick={() => setNewPromo({...newPromo, promo_type: 'bogo'})}
                      className="flex-1"
                    >
                      <Gift className="h-4 w-4 mr-2" />
                      BOGO
                    </Button>
                    <Button
                      type="button"
                      variant={newPromo.promo_type === 'percentage' ? 'default' : 'outline'}
                      onClick={() => setNewPromo({...newPromo, promo_type: 'percentage'})}
                      className="flex-1"
                    >
                      <Percent className="h-4 w-4 mr-2" />
                      % Discount
                    </Button>
                  </div>
                </div>
                {newPromo.promo_type === 'bogo' && (
                  <div className="space-y-2">
                    <Label htmlFor="purchaseLimit">Purchase Limit</Label>
                    <Input
                      id="purchaseLimit"
                      type="number"
                      value={newPromo.purchase_limit}
                      onChange={(e) => setNewPromo({...newPromo, purchase_limit: parseInt(e.target.value) || 0})}
                      placeholder="e.g., 1000"
                    />
                    <p className="text-sm text-stone-500">Promo ends after this many purchases</p>
                  </div>
                )}
                {newPromo.promo_type === 'percentage' && (
                  <div className="space-y-2">
                    <Label htmlFor="discountPercent">Discount Percentage</Label>
                    <Input
                      id="discountPercent"
                      type="number"
                      value={newPromo.discount_percent}
                      onChange={(e) => setNewPromo({...newPromo, discount_percent: parseInt(e.target.value) || 0})}
                      placeholder="e.g., 20"
                    />
                    <p className="text-sm text-stone-500">Percentage off all credit packages</p>
                  </div>
                )}
                
                {/* Campaign Duration */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Campaign Duration (Optional)
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="startDate">Start Date</Label>
                      <Input
                        id="startDate"
                        type="date"
                        value={newPromo.start_date}
                        onChange={(e) => setNewPromo({...newPromo, start_date: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="endDate">End Date</Label>
                      <Input
                        id="endDate"
                        type="date"
                        value={newPromo.end_date}
                        onChange={(e) => setNewPromo({...newPromo, end_date: e.target.value})}
                      />
                    </div>
                  </div>
                  <p className="text-sm text-stone-500 mt-2">
                    Set dates to show &quot;Limited Time Offer&quot; on the landing page. Leave blank for no time limit display.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowCreatePromoDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (!newPromo.name) {
                      toast.error("Please enter a promo name");
                      return;
                    }
                    setSavingPromo(true);
                    try {
                      const token = localStorage.getItem("token");
                      await axios.post(`${API}/admin/promos`, newPromo, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success("Promo created! Activate it when ready.");
                      setShowCreatePromoDialog(false);
                      setNewPromo({ name: "", promo_type: "bogo", purchase_limit: 1000, discount_percent: 20 });
                      loadAllData();
                    } catch (error) {
                      toast.error(error.response?.data?.detail || "Failed to create promo");
                    } finally {
                      setSavingPromo(false);
                    }
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={savingPromo}
                >
                  {savingPromo ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create Promo"
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Edit Credits Dialog */}
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit User Credits</DialogTitle>
                <DialogDescription>
                  Update credits for {selectedUser?.email}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Current Credits: {selectedUser?.credits?.toFixed(1) || 0}</Label>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="credits">New Credit Amount</Label>
                  <Input
                    id="credits"
                    type="number"
                    value={editCredits}
                    onChange={(e) => setEditCredits(e.target.value)}
                    placeholder="Enter new credit amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reason">Reason (optional)</Label>
                  <Input
                    id="reason"
                    value={editReason}
                    onChange={(e) => setEditReason(e.target.value)}
                    placeholder="e.g., Refund, Bonus, Correction"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleUpdateCredits} className="flex-1">
                  Update Credits
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Delete User Confirmation Dialog */}
          <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-red-600">Delete User</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete <strong>{userToDelete?.email}</strong>?
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
                  <p className="font-semibold mb-2">⚠️ This action cannot be undone!</p>
                  <p>This will permanently delete:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>The user account</li>
                    <li>All their books and chapters</li>
                    <li>All their cover projects</li>
                    <li>All their scripts</li>
                  </ul>
                </div>
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowDeleteDialog(false);
                    setUserToDelete(null);
                  }} 
                  className="flex-1"
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={confirmDeleteUser} 
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete User
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Password Reset Dialog */}
          <Dialog open={showPasswordResetDialog} onOpenChange={setShowPasswordResetDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Reset User Password</DialogTitle>
                <DialogDescription>
                  Set a new password for {passwordResetUser?.email}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 7 characters)"
                    data-testid="admin-reset-password-input"
                  />
                </div>
                <p className="text-sm text-stone-500">
                  The user will need to use this password to log in. Consider sharing it securely.
                </p>
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowPasswordResetDialog(false);
                    setPasswordResetUser(null);
                    setNewPassword("");
                  }} 
                  className="flex-1"
                  data-testid="admin-reset-password-cancel"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleResetPassword} 
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                  disabled={resettingPassword}
                  data-testid="admin-reset-password-confirm"
                >
                  {resettingPassword ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reset Password
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default AdminPortal;
